import { useCallback, useEffect, useState } from "react";
import { createAnnouncement, getAnnouncements, retireAnnouncement, toMessage } from "../lib/api";
import type { AnnouncementRecord } from "../lib/types";

/**
 * The announcement composer: one short notice shown on the landing page.
 *
 * Deliberately optimistic-free: posting and clearing wait for the server, then
 * reload, so what is shown here is what visitors actually see.
 */
export default function OperatorControls() {
  const [announcements, setAnnouncements] = useState<AnnouncementRecord[]>([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const list = await getAnnouncements(signal);
      setAnnouncements(list ?? []);
    } catch (err) {
      if (!signal?.aborted) setError(toMessage(err));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const post = useCallback(async () => {
    const text = message.trim();
    if (!text || busy) return;
    setBusy("announce");
    setError(null);
    try {
      await createAnnouncement({ message: text });
      setMessage("");
      await load();
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setBusy(null);
    }
  }, [busy, load, message]);

  const retire = useCallback(
    async (id: string) => {
      if (busy) return;
      setBusy(id);
      setError(null);
      try {
        await retireAnnouncement(id);
        await load();
      } catch (err) {
        setError(toMessage(err));
      } finally {
        setBusy(null);
      }
    },
    [busy, load]
  );

  const live = announcements.find((a) => a.active);

  return (
    <>
      <section className="aw-ad-section">
        <div className="aw-ad-section-head">
          <h2 className="aw-ad-h2">Announcement</h2>
          <span className="aw-ad-h2-sub">shown to everyone on the landing page</span>
        </div>

        <div className="ap-announce-form">
          <textarea
            className="ap-input"
            value={message}
            maxLength={500}
            rows={2}
            placeholder="Something everyone should see…"
            onChange={(e) => setMessage(e.target.value)}
          />
          <div className="ap-announce-form__foot">
            <span className="ap-muted">{message.length}/500</span>
            <button
              type="button"
              className="ap-btn ap-btn--sm"
              disabled={!message.trim() || busy === "announce"}
              onClick={() => void post()}
            >
              {busy === "announce" ? "Posting…" : "Post"}
            </button>
          </div>
        </div>

        {live ? (
          <div className="ap-announce ap-announce--admin">
            <span className="ap-announce__dot" aria-hidden="true" />
            <p>{live.message}</p>
            <button
              type="button"
              className="ap-btn ap-btn--sm ap-btn--ghost"
              disabled={busy === live._id}
              onClick={() => void retire(live._id)}
            >
              {busy === live._id ? "Clearing…" : "Clear"}
            </button>
          </div>
        ) : (
          <p className="ap-muted">Nothing is showing right now.</p>
        )}

        {error && <p className="ap-muted ap-announce__err">{error}</p>}
      </section>
    </>
  );
}
